import { PublicRoutes } from "@/constants/routes";
import { Clock, Cloud, ServerCog, ShieldCheck } from "lucide-react";
import type { LegalDocument } from "./types";

/**
 * Relatório público do incidente de 16/10/2026. Página fechada: sem header,
 * sem footer e sem links para o resto do site — é enviada por link a quem foi
 * afetado, e fica em `noindex`.
 *
 * Fica de fora de propósito: IP da máquina, porta e usuário de acesso. Detalhe
 * de infraestrutura num texto público só ajuda quem quer atacar.
 */
export const INCIDENT_2026_10_16: LegalDocument = {
  key: "incidente",
  path: PublicRoutes.INCIDENTE_2026_10_16,
  eyebrow: "Relatório de incidente",
  title: "O que aconteceu com a Fidexa em 16 de outubro",
  seoTitle: "Incidente de 16/10/2026: o que aconteceu",
  description:
    "A Fidexa ficou fora do ar por cerca de 30 minutos em 16/10/2026, durante manutenção emergencial do provedor. Sem ataque e sem vazamento de dados. Entenda.",
  intro:
    "Na manhã de 16 de outubro, a Fidexa ficou fora do ar por cerca de 30 minutos durante uma manutenção emergencial do nosso provedor de nuvem. Aqui está o que aconteceu, o que fizemos e o que vamos mudar.",
  version: "1.0",
  updatedAt: "2026-10-16",

  highlights: [
    {
      icon: ServerCog,
      title: "O que houve",
      text: "Nosso provedor de nuvem atualizou o kernel dos servidores para corrigir uma falha crítica de segurança.",
    },
    {
      icon: Clock,
      title: "Quanto durou",
      text: "Cerca de 30 minutos, das 10h40 às 11h13 (horário de Brasília).",
    },
    {
      icon: ShieldCheck,
      title: "Seus dados",
      text: "**Não houve ataque hacker nem vazamento de dados.** Os dados das lojas e dos clientes seguem íntegros.",
    },
    {
      icon: Cloud,
      title: "O que muda",
      text: "Vamos estudar redundância em um segundo provedor de nuvem, para que a manutenção de um só não tire a Fidexa do ar.",
    },
  ],

  sections: [
    {
      id: "resumo",
      title: "Resumo",
      summary:
        "Uma manutenção emergencial do provedor reiniciou os servidores e deixou a Fidexa inacessível por cerca de 30 minutos.",
      blocks: [
        {
          type: "paragraph",
          text: "Em 16 de outubro de 2026, nosso provedor de nuvem aplicou em caráter emergencial a correção da vulnerabilidade **CVE-2026-99999**, classificada como crítica. A correção exigiu atualizar o kernel dos servidores, e cada máquina precisou reiniciar.",
        },
        {
          type: "paragraph",
          text: "Durante esse período, **ninguém conseguiu se conectar à Fidexa**. A plataforma voltou ao normal às 11h13, sem perda de dados.",
        },
      ],
    },
    {
      id: "linha-do-tempo",
      title: "Linha do tempo",
      summary:
        "Das 10h40 às 11h13 de 16 de outubro de 2026, no horário de Brasília.",
      blocks: [
        {
          type: "timeline",
          caption: "Linha do tempo do incidente, em horário de Brasília",
          items: [
            {
              time: "10h40",
              tone: "danger",
              text: "O provedor de nuvem inicia a manutenção emergencial e o servidor da Fidexa deixa de responder. A estimativa do provedor era de 10 a 20 minutos de interrupção.",
            },
            {
              time: "10h45",
              text: "Publicamos um aviso na tela de login sobre a atualização em andamento.",
            },
            {
              time: "10h56",
              text: "O servidor reinicia já com o kernel corrigido.",
            },
            {
              time: "10h56 a 11h13",
              text: "O provedor conclui a sincronização da rede e nós conferimos o funcionamento da plataforma.",
            },
            {
              time: "11h13",
              tone: "success",
              text: "Tudo normalizado. Retiramos o aviso da tela de login.",
            },
          ],
        },
      ],
    },
    {
      id: "causa",
      title: "Causa",
      summary:
        "A correção urgente de uma falha de segurança no provedor. Não foi um ataque à Fidexa.",
      blocks: [
        {
          type: "paragraph",
          text: "A interrupção foi causada pela atualização de kernel que o provedor aplicou nos próprios servidores para corrigir a CVE-2026-99999. A manutenção foi feita pelo provedor, em todas as máquinas afetadas, e não teve origem em nenhuma mudança da Fidexa.",
        },
        {
          type: "paragraph",
          text: "O ponto fraco do nosso lado é outro: hoje a Fidexa depende de um único provedor de nuvem. Quando esse provedor para, a Fidexa para junto. A responsabilidade de manter a plataforma no ar é nossa, mesmo quando a causa está fora de casa.",
        },
      ],
    },
    {
      id: "seus-dados",
      title: "Seus dados",
      summary: "Não houve ataque hacker, invasão nem vazamento de dados.",
      blocks: [
        {
          type: "callout",
          tone: "success",
          title: "Sem ataque e sem vazamento",
          text: "O incidente foi uma manutenção do provedor, não uma invasão. Nenhum dado de lojas ou de clientes foi exposto, e as informações seguem íntegras.",
        },
        {
          type: "paragraph",
          text: "A atualização existiu justamente para fechar uma falha de segurança. O servidor da Fidexa voltou ao ar já com a correção aplicada.",
        },
      ],
    },
    {
      id: "impacto",
      title: "Impacto",
      summary:
        "Por cerca de 30 minutos, não foi possível se conectar à Fidexa.",
      blocks: [
        {
          type: "paragraph",
          text: "Entre 10h40 e 11h13, a plataforma ficou inteira fora do ar: não era possível entrar no sistema nem usar nenhuma de suas funções. Fora desse intervalo, a Fidexa funcionou normalmente.",
        },
      ],
    },
    {
      id: "o-que-fizemos",
      title: "O que fizemos",
      summary:
        "Avisamos na tela de login, acompanhamos o provedor e conferimos a plataforma antes de dar o incidente por encerrado.",
      blocks: [
        {
          type: "list",
          items: [
            "**Aviso na tela de login** cinco minutos depois da queda, para quem tentasse entrar saber o que estava acontecendo.",
            "**Acompanhamento junto ao provedor** durante toda a manutenção, até o servidor voltar.",
            "**Conferência da plataforma** depois do reinício, antes de retirar o aviso.",
          ],
        },
      ],
    },
    {
      id: "proximos-passos",
      title: "O que vamos fazer",
      summary:
        "Estudar redundância em outra nuvem, para que uma manutenção como essa não tire a Fidexa do ar.",
      blocks: [
        {
          type: "list",
          items: [
            "**Redundância em um segundo provedor de nuvem.** Vamos estudar como manter a Fidexa funcionando em mais de um provedor, para que a parada de um não interrompa o serviço.",
            "**Aviso mais rápido.** Vamos deixar pronto o aviso de instabilidade, para que ele apareça assim que houver um problema.",
          ],
        },
        {
          type: "paragraph",
          text: "Pedimos desculpas a quem precisou da Fidexa nesse período. Se você teve algum problema por causa da interrupção, fale com o nosso suporte pelo canal de sempre.",
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-16",
      changes: ["Publicação do relatório."],
    },
  ],
};
